import { Link, useNavigate } from 'react-router-dom';
import { Chip, DropdownMenuItem } from '@c2fo/liquidity';
import { EyeIcon, PencilIcon } from '@c2fo/liquidity/icons';
import { ScheduledDeliveryDisplay } from '../../api/ScheduledDeliveries';
import { DataGrid, DataGridCard } from '../DataGrid';
import { ActionsMenu } from '../ActionsMenu';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

interface ScheduleGridProps {
  schedules: ScheduledDeliveryDisplay[];
}

export const ScheduleGrid: React.FC<ScheduleGridProps> = ({ schedules }) => {
  const navigate = useNavigate();

  return (
    <DataGrid>
      {schedules.map((schedule) => (
        <DataGridCard key={schedule.uid}>
          <div className="flex items-center justify-between">
            <Chip
              label={schedule.scheduleTypeLabel}
              type={schedule.scheduleTypeLabel === 'Cron' ? 'primary' : 'secondary'}
              variant="outlined"
              size="xs"
            />
            <ActionsMenu>
              <DropdownMenuItem
                className="active:bg-lightBlue-100"
                onSelect={() => navigate(`/file-delivery/scheduledDeliveries/${schedule.uid}`)}
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem
                className="active:bg-lightBlue-100"
                onSelect={() => navigate(`/file-delivery/scheduledDeliveries/${schedule.uid}/edit`)}
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            </ActionsMenu>
          </div>

          {schedule.displayName && (
            <div className="text-sm font-medium text-gray-900 truncate">{schedule.displayName}</div>
          )}

          <div className="flex flex-col gap-1 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <span className="font-medium">Source:</span>
              <Link
                to={`/file-delivery/${schedule.sourceLocation?.name}`}
                className="text-primary-600 hover:text-primary-700 hover:underline truncate"
              >
                {schedule.sourceLocation?.displayName ?? 'Unknown'}
              </Link>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Target:</span>
              <Link
                to={`/file-delivery/${schedule.targetLocation?.name}`}
                className="text-primary-600 hover:text-primary-700 hover:underline truncate"
              >
                {schedule.targetLocation?.displayName ?? 'Unknown'}
              </Link>
            </div>
          </div>

          <div className="text-xs text-gray-500">
            <span className="font-medium">Pattern:</span>{' '}
            <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{schedule.sourcePath}</code>
          </div>

          <div className="text-xs text-gray-500">
            <span className="font-medium">Schedule:</span> {schedule.scheduleDescription}
          </div>

          <div className="flex flex-col gap-1 text-xs text-gray-500">
            <div>
              <span className="font-medium">Created:</span>{' '}
              {schedule.createTime ? formatDateWithTimezone(schedule.createTime) : '-'}
            </div>
          </div>
        </DataGridCard>
      ))}
    </DataGrid>
  );
};
